import React, { useEffect, useMemo } from 'react'
import { Address, Combobox } from 'medere-ui-framework'
import { Chip } from 'primereact/chip'
import { useLocationService } from '../servicesHooks/Location/useLocationService'
import { ComboDataDTO } from '../dto/ComboDataDTO'

interface FiscalAddressComboProps {
  fiscalAddress: ComboDataDTO | null
  onChange: (value: ComboDataDTO | null) => void
  error?: boolean
  medereentity: number
  localAddresses?: Address[]
  disabled?: boolean
}

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function normalizeAddressLabel(address: Address): string {
  return (address?.addressdesc ?? '').trim()
}

function normalizeComboName(name: unknown): string {
  return String(name ?? '').trim()
}

function normalizeTypeDescription(type: unknown): string {
  if (!isRecord(type)) return ''
  const description = typeof type.description === 'string'
    ? type.description
    : (typeof type.name === 'string' ? type.name : '')
  return normalizeComboName(description).toLowerCase()
}

function isFiscalAddress(address: Address): boolean {
  const types = address?.types ?? []
  return types.some(type => {
    const id = Number(type?.id ?? -1)
    const description = normalizeTypeDescription(type)
    return id === 3 || description.includes('fiscal')
  })
}

function normalizeComboItem(item: ComboDataDTO | null | undefined): ComboDataDTO | null {
  const id = Number(item?.id ?? -1)
  const name = normalizeComboName(item?.name)
  if (!Number.isFinite(id) || id <= 0 || name.length === 0) {
    return null
  }
  return { id, name }
}

function normalizeComboFromUnknown(item: unknown): ComboDataDTO | null {
  if (!isRecord(item)) return null
  const id = Number(item.id ?? -1)
  const name = normalizeComboName(item.name)
  return normalizeComboItem({ id, name })
}

export default function FiscalAddressCombo({
  fiscalAddress,
  onChange,
  error,
  medereentity,
  localAddresses = [],
  disabled = false,
}: Readonly<FiscalAddressComboProps>) {
  const { getAllAddressByMedereEntityAndType } = useLocationService()
  const [fiscalAddressesFromApi, setFiscalAddressesFromApi] = React.useState<ComboDataDTO[]>([])

  useEffect(() => {
    if (!medereentity || medereentity <= 0) {
      setFiscalAddressesFromApi([])
      return
    }

    getAllAddressByMedereEntityAndType({
      offSet: 0,
      pageSize: 50,
      params: { medereentity, addresstype: 3 },
    }).then((res) => {
      const normalized = (res ?? [])
        .map((item) => normalizeComboFromUnknown(item))
        .filter((item): item is ComboDataDTO => item !== null)
      setFiscalAddressesFromApi(normalized)
    }).catch(() => setFiscalAddressesFromApi([]))
  }, [medereentity])

  const fiscalAddressesFromLocal = useMemo(() => {
    return (localAddresses ?? [])
      .filter(address => isFiscalAddress(address))
      .map(address => normalizeComboItem({
        id: address.address,
        name: normalizeAddressLabel(address),
      }))
      .filter((item): item is ComboDataDTO => item !== null)
  }, [localAddresses])

  const fiscalAddresses = useMemo(() => {
    const merged = new Map<number, ComboDataDTO>()
    for (const item of [...fiscalAddressesFromApi, ...fiscalAddressesFromLocal]) {
      const normalized = normalizeComboItem(item)
      if (!normalized) continue
      if (!merged.has(normalized.id as number)) {
        merged.set(normalized.id as number, normalized)
      }
    }
    return Array.from(merged.values())
  }, [fiscalAddressesFromApi, fiscalAddressesFromLocal])

  const normalizedFiscalAddress = normalizeComboItem(fiscalAddress)
  const fiscalAddressName = normalizeComboName(fiscalAddress?.name)
  const selectedFiscalAddress = normalizedFiscalAddress
    ? fiscalAddresses.find(item => item.id === normalizedFiscalAddress.id) ?? normalizedFiscalAddress
    : (fiscalAddressName.length > 0
        ? fiscalAddresses.find(item => normalizeComboName(item.name) === fiscalAddressName) ?? null
        : null)

  function handleChange(value: ComboDataDTO | null) {
    onChange(normalizeComboItem(value))
  }

  return <div className='flex my-2 align-items-baseline'>
    <p className='label mr-2'>Domicilio fiscal</p>
    {fiscalAddresses.length > 0
      ? <Combobox
          value={selectedFiscalAddress}
          setValue={handleChange}
          error={error}
          list={fiscalAddresses}
          optionLabel='name'
          className='w-8'
          disable={disabled}
        />
      : <Chip
          label={'Direcci\u00f3n fiscal faltante'}
          className='bg-bluegray-50 border-round ml-2'
          pt={{ label: { className: 'text-xs font-semibold my-1 info-dark' } }}
        />}
  </div>
}
